import React, { useState, Component } from 'react';
import { ZapIcon, FlameIcon, MoonIcon, HeartIcon, BookOpenIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, BookmarkIcon, LinkIcon, GlobeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
const burnLevelData = [{
  id: 'slow',
  name: 'Slow Burn',
  level: 1,
  count: 0,
  description: 'The romantic tension builds gradually over the entire story. Expect longing glances, near-misses, and delayed gratification that makes the payoff all the sweeter.',
  examples: ['Enemies to Lovers', 'Friends to Lovers', 'Pining'],
  color: {
    light: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    dark: 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
  },
  iconColor: 'text-blue-500'
}, {
  id: 'medium',
  name: 'Medium Burn',
  level: 2,
  count: 0,
  description: 'A balanced pace — some early tension with a satisfying mid-story payoff. The romance develops naturally without dragging or rushing.',
  examples: ['Second Chance', 'Forced Proximity', 'Fake Dating'],
  color: {
    light: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    dark: 'bg-amber-900/30 text-amber-300 hover:bg-amber-900/50'
  },
  iconColor: 'text-amber-500'
}, {
  id: 'fast',
  name: 'Fast Burn',
  level: 3,
  count: 0,
  description: 'The romance ignites quickly. Feelings develop early and intensely — perfect for readers who want the heat without the wait.',
  examples: ['Instalove', 'Insta-Lust', 'One Night Stand'],
  color: {
    light: 'bg-red-50 text-red-700 hover:bg-red-100',
    dark: 'bg-red-900/30 text-red-300 hover:bg-red-900/50'
  },
  iconColor: 'text-red-500'
}];
const contentNavItems: NavItem[] = [{
  label: 'View All Tags',
  href: '/tagged-books?type=all',
  icon: ListIcon
}, {
  label: 'Tropes',
  href: '/tropes',
  icon: HeartHandshakeIcon,
  subItems: [{
    label: 'Romance',
    href: '/tropes?category=romance'
  }, {
    label: 'Fantasy',
    href: '/tropes?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/tropes?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/tropes?category=scifi'
  }, {
    label: 'Horror',
    href: '/tropes?category=horror'
  }, {
    label: 'General',
    href: '/tropes?category=general'
  }]
}, {
  label: 'Characters',
  href: '/characters',
  icon: UserIcon,
  subItems: [{
    label: 'Romance',
    href: '/characters?category=romance'
  }, {
    label: 'Fantasy',
    href: '/characters?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/characters?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/characters?category=scifi'
  }, {
    label: 'Horror',
    href: '/characters?category=horror'
  }, {
    label: 'General',
    href: '/characters?category=general'
  }]
}, {
  label: 'Relationship Types',
  href: '/relationship-types',
  icon: UsersIcon,
  subItems: [{
    label: 'Romance',
    href: '/relationship-types?category=romance'
  }, {
    label: 'Fantasy',
    href: '/relationship-types?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/relationship-types?category=mystery'
  }, {
    label: 'General',
    href: '/relationship-types?category=general'
  }]
}, {
  label: 'Pairing Types',
  href: '/pairing-types',
  icon: LinkIcon,
  subItems: [{
    label: 'Monogamous',
    href: '/pairing-types?category=monogamous'
  }, {
    label: 'Why Choose',
    href: '/pairing-types?category=why-choose'
  }, {
    label: 'Closed Triad',
    href: '/pairing-types?category=closed-triad'
  }, {
    label: 'Polyamorous',
    href: '/pairing-types?category=polyamorous'
  }, {
    label: 'NB / Trans Inclusive',
    href: '/pairing-types?category=nb-trans'
  }, {
    label: 'No Romance',
    href: '/pairing-types?category=no-romance'
  }]
}, {
  label: 'Spice & Darkness',
  href: '/spice-darkness',
  icon: FlameIcon,
  subItems: [{
    label: 'Spice Levels',
    href: '/spice-darkness#spice'
  }, {
    label: 'Darkness Levels',
    href: '/spice-darkness#darkness'
  }]
}, {
  label: 'Burn Levels',
  href: '/burn-levels',
  icon: ZapIcon,
  isActive: true
}, {
  label: 'Ending Types',
  href: '/ending-types',
  icon: BookmarkIcon
}, {
  label: 'Representation',
  href: '/representation',
  icon: UsersIcon,
  subItems: [{
    label: 'Sexual Orientation & Gender',
    href: '/representation?category=Sexual+Orientation+%26+Gender'
  }, {
    label: 'Race & Ethnicity',
    href: '/representation?category=Race+%26+Ethnicity'
  }, {
    label: 'Neurodiversity & Disability',
    href: '/representation?category=Neurodiversity+%26+Disability'
  }, {
    label: 'Mental Health',
    href: '/representation?category=Mental+Health'
  }]
}, {
  label: 'Cultural Identity',
  href: '/cultural-identity',
  icon: GlobeIcon
}, {
  label: 'Themes',
  href: '/themes',
  icon: BookOpenIcon,
  subItems: [{
    label: 'Personal Growth',
    href: '/themes?category=Personal+Growth'
  }, {
    label: 'Destiny',
    href: '/themes?category=Destiny'
  }, {
    label: 'Power',
    href: '/themes?category=Power'
  }, {
    label: 'Morality',
    href: '/themes?category=Morality'
  }, {
    label: 'Relationships',
    href: '/themes?category=Relationships'
  }, {
    label: 'Identity',
    href: '/themes?category=Identity'
  }]
}, {
  label: 'Settings',
  href: '/settings',
  icon: MapPinIcon,
  subItems: [{
    label: 'Contemporary',
    href: '/settings?category=Contemporary'
  }, {
    label: 'Historical',
    href: '/settings?category=Historical'
  }, {
    label: 'Fantasy',
    href: '/settings?category=Fantasy'
  }, {
    label: 'Science Fiction',
    href: '/settings?category=Science%20Fiction'
  }]
}, {
  label: 'Content Warnings',
  href: '/content-warnings',
  icon: AlertCircleIcon
}];
const BurnLevels: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  return <div className={dk.page} data-id="element-8053" data-mp="3vswtw5ntjxah">
      <ContentPageHeader title="Burn Levels" icon={getContentPageIcon('burnlevels')} searchPlaceholder="Search burn levels..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={showFilters} setShowFilters={setShowFilters} data-id="element-8054" data-mp="3vtoqnjt5k12c" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8055" data-mp="dod1nn8nbjh8">
        <div className="flex gap-8" data-id="element-8056" data-mp="1k3mdltp3bn38">
          <FilterSidebarWrapper isOpen={showFilters} onClose={() => setShowFilters(false)} isCollapsed={false} onToggleCollapse={() => {}} data-id="element-8057" data-mp="1k2kpoomz7643">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => {}} searchable={false} data-id="element-8058" data-mp="10qonb8ok0i5n" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-8059" data-mp="19k1qd3gc105g">
            <div className="text-center mb-8" data-id="element-8060" data-mp="14qflvykbo2kq">
              <h1 className={`text-2xl sm:text-3xl font-bold flex justify-center items-center gap-2 ${dk.textHeading}`} data-id="element-8061" data-mp="14pe132di81pt">
                <ZapIcon className="h-6 w-6 text-amber-500" data-id="element-8062" data-mp="1eg8ls2b3qt95" />
                Burn Levels
              </h1>
              <p className={`text-base sm:text-lg max-w-2xl mx-auto mt-3 ${dk.textSecondary}`} data-id="element-8063" data-mp="1sfrt4sw3a0ke">
                How quickly does the romance heat up? Find books that match your
                preferred pace of romantic development.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6" data-id="element-8064" data-mp="3q85t3xcbsbp0">
              {burnLevelData.map(level => <div key={level.id} className={`rounded-lg shadow-md p-4 sm:p-6 ${dk.card}`} data-id="element-8065" data-mp="3g477jv1y6yqh">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4" data-id="element-8066" data-mp="36d46gt90qjr7">
                    <h2 className={`text-lg sm:text-xl font-semibold flex items-center ${dk.textHeading}`} data-id="element-8067" data-mp="3bbe4dbm4nygi">
                      <div className="flex space-x-1 mr-3" data-id="element-8068" data-mp="2wh1900p439al">
                        {[...Array(level.level)].map((_, i) => <ZapIcon key={i} className={`h-4 w-4 sm:h-5 sm:w-5 ${level.iconColor}`} fill="currentColor" data-id="element-8069" data-mp="2nei7bjx2b2px" data-mpi={i} />)}
                      </div>
                      {level.name}
                    </h2>
                    <Link to={`/tagged-books?tag=${encodeURIComponent(level.name)}&category=burn`} className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${dk.badgeNeutral} transition-colors`} data-id="element-8070" data-mp="qkngt0rvs299">
                      {level.count} books
                    </Link>
                  </div>
                  <p className={`text-sm sm:text-base mb-3 sm:mb-4 ${dk.textSecondary}`} data-id="element-8071" data-mp="3ncoyokvk839v">
                    {level.description}
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-8072" data-mp="jx03c9d30apd">
                    {level.examples.map((example, index) => <Link key={index} to={`/tagged-books?tag=${encodeURIComponent(example)}&category=burn&level=${encodeURIComponent(level.name)}`} className={`inline-flex items-center px-2 sm:px-3 py-1 ${rt.badgeRadius} text-xs sm:text-sm ${darkMode ? level.color.dark : level.color.light} transition-colors`} data-id="element-8073" data-mp="5w779k74t7k5" data-mpi={index}>
                        {example}
                      </Link>)}
                  </div>
                </div>)}
            </div>
          </main>
        </div>
      </div>
    </div>;
};
export default BurnLevels;